import React, { useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Spinner, Pagination } from 'react-bootstrap';
import styles from './ChangeVoucherThemeModal.module.scss';
import useComponentState from '../../utils/useComponentState';
import useDebounce from '../../utils/useDebounce';
import { searchVoucherThemeDropdownList } from '../../api/voucherTheme';

const FILTER_OPTIONS = [
  { value: 'NAME', label: 'Filter by Voucher Theme Name' },
];

const ChangeVoucherThemeModal = ({
  show,
  handleClose,
  token,
  onThemeSelect,
  currentTheme,
}) => {
  const [state, setState] = useComponentState({
    searchTerm: '',
    themes: [],
    loading: false,
    error: '',
    filterType: 'name',
    sortBy: 'name',
    sortDirection: 'asc',
    currentPage: 0,
    pageLimit: 10,
  });

  const debouncedSearchTerm = useDebounce(state.searchTerm, 500);

  // Track the latest request so stale responses are ignored
  const latestReqIdRef = useRef(0);
  // support aborting older requests if api util accepts AbortController
  const abortCtrlRef = useRef(null);

  useEffect(() => {
    const term = debouncedSearchTerm.trim();

    // Clear results if empty search
    if (!term) {
      setState({
        themes: [],
        error: '',
        loading: false,
        currentPage: 0,
      });
      // cancel any previous in-flight call
      if (abortCtrlRef.current) abortCtrlRef.current.abort();
      return;
    }

    const reqId = ++latestReqIdRef.current;

    // If searchProducts supports axios signal, wire it in
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    setState({ loading: true, error: '' });

    (async () => {
      try {
        const response = await searchVoucherThemeDropdownList(
          token,
          term,
          state.filterType,
          state.sortBy,
          state.sortDirection
        );
        // Ignore stale responses
        if (reqId !== latestReqIdRef.current) return;

        setState({
          themes: response?.voucherThemes || [],
          loading: false,
          totalCount: response?.totalCount || 0,
          pageCount: response?.pageCount || 0,
          hasNext: response?.hasNext || false,
          hasPrevious: response?.hasPrevious || false,
        });
      } catch (err) {
        // Ignore errors from aborted/stale requests
        if (reqId !== latestReqIdRef.current) return;
        if (err?.name === 'AbortError') return;

        setState({
          error: err?.message || 'Failed to fetch voucher themes',
          loading: false,
        });
      }
    })();

    // Cleanup: abort when inputs change/unmount
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, state.filterType, state.currentPage, token]);

  const handleCloseWithStateReset = useCallback(() => {
    handleClose();
    setState({
      searchTerm: '',
      themes: [],
      error: '',
      loading: false,
      currentPage: 0,
    });
    latestReqIdRef.current = 0;
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
    }
  }, [handleClose, setState]);

  const handleThemeSelect = useCallback(
    (theme) => {
      onThemeSelect(theme);
      handleCloseWithStateReset();
    },
    [onThemeSelect, handleCloseWithStateReset]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setState({ currentPage: newPage });
    },
    [setState]
  );

  return (
    <Modal
      className={styles.changeVoucherThemeModal}
      show={show}
      onHide={handleCloseWithStateReset}
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>Change Voucher Theme</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentTheme && (
          <div className={styles.ThemeInfo}>
            <h6 className='mb-3'>Current Voucher Theme</h6>
            <div className='mb-3 p-3 border rounded'>
              <div className='mb-2'>
                <strong>Name:</strong> {currentTheme.name || 'N/A'}
              </div>
              {currentTheme.imageFront && (
                <div>
                  <strong>Preview:</strong>
                  <br />
                  <img
                    src={currentTheme.imageFront}
                    alt='Current Theme'
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      marginTop: '8px',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.ThemeInfo}>
          <h6 className='mb-3'>Select New Voucher Theme</h6>
          <div className='d-flex gap-2 mb-3'>
            <Form.Group style={{ minWidth: '150px' }}>
              <Form.Control
                as='select'
                className='form-select'
                value={state.filterType}
                onChange={(e) => setState({ filterType: e.target.value })}
              >
                {/* Render filter options */}
                {FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group className='flex-grow-1'>
              <Form.Control
                type='text'
                placeholder='Search voucher themes...'
                value={state.searchTerm}
                onChange={(e) => setState({ searchTerm: e.target.value })}
              />
            </Form.Group>
          </div>
        </div>

        {state.loading && (
          <div className={styles.loading}>
            <Spinner animation='border' size='sm' />
            <span style={{ marginLeft: '10px' }}>Loading themes...</span>
          </div>
        )}
        {/* {state.error && <div className={styles.error}>{state.error}</div>} */}

        <div
          className={`${styles.themesList} ${
            state.themes.length === 0 ? styles.noBorder : ''
          }`}
        >
          {state.themes.length === 0 && !state.loading && state.searchTerm ? (
            <div className={`text-center text-muted ${styles.selectedTags}`}>
              No themes found
            </div>
          ) : (
            state.themes.map((theme) => (
              <div
                key={theme.id}
                className={styles.themeItem}
                onClick={() => handleThemeSelect(theme)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleThemeSelect(theme);
                  }
                }}
              >
                <div className={styles.themeContent}>
                  {(theme.image || theme.imageFront) && (
                    <img
                      src={theme.image || theme.imageFront}
                      alt={theme.name}
                      className={styles.themeImage}
                    />
                  )}
                  <div className={styles.themeDetails}>
                    <span className={styles.themeName}>{theme.name}</span>
                    <span className={styles.themeId}>Theme ID: {theme.id}</span>
                  </div>
                </div>
                {currentTheme?.id === theme.id && (
                  <span className={styles.current}>(Current)</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination - only show if there are multiple pages */}
        {state.pageCount > 1 && (
          <div
            className={`mt-4 d-flex justify-content-center ${styles.pagination}`}
          >
            <Pagination>
              <Pagination.Prev
                disabled={!state.hasPrevious || state.loading}
                onClick={() => handlePageChange(state.currentPage - 1)}
              />
              {Array.from({ length: state.pageCount }, (_, idx) => (
                <Pagination.Item
                  key={idx}
                  active={idx === state.currentPage}
                  disabled={state.loading}
                  onClick={() => handlePageChange(idx)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={!state.hasNext || state.loading}
                onClick={() => handlePageChange(state.currentPage + 1)}
              />
            </Pagination>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleCloseWithStateReset}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeVoucherThemeModal;
